/**
 * The dashboard is a single-page app: a tab left open keeps running the build it loaded, however
 * many times we deploy. This notices a newer build and moves the tab onto it.
 *
 * How: every build fingerprints its bundle name (assets/index-XXXX.js) and index.html points at it.
 * We re-read index.html (never from cache) and compare. When it differs:
 *   - if the tab was in the background, reload as the user comes back to it (nothing to lose);
 *   - if they are looking at it, show a small "Refresh" pill instead of yanking the page away.
 */
const CHECK_EVERY_MS = 5 * 60 * 1000;

function currentBundle(): string | null {
  const s = [...document.scripts].map(x => x.src).find(src => /\/assets\/index-[^/]+\.js/.test(src));
  return s ? s.split('/').pop()! : null;
}

async function latestBundle(): Promise<string | null> {
  try {
    const res = await fetch(`/index.html?v=${Date.now()}`, { cache: 'no-store' });
    if (!res.ok) return null;
    const m = (await res.text()).match(/assets\/(index-[^"']+\.js)/);
    return m ? m[1] : null;
  } catch {
    return null; // offline, or the network blinked: try again next time
  }
}

function showRefreshPill() {
  if (document.getElementById('obdai-new-version')) return;
  const b = document.createElement('button');
  b.id = 'obdai-new-version';
  b.type = 'button';
  b.textContent = 'New version available · Refresh';
  b.style.cssText = [
    'position:fixed', 'left:50%', 'bottom:20px', 'transform:translateX(-50%)', 'z-index:9999',
    'padding:10px 18px', 'border-radius:999px', 'border:0', 'cursor:pointer',
    'font:600 13px system-ui,sans-serif', 'color:#fff',
    'background:linear-gradient(135deg,#6d7cff,#e866dd)', 'box-shadow:0 8px 24px rgba(0,0,0,.35)',
  ].join(';');
  b.onclick = () => window.location.reload();
  document.body.appendChild(b);
}

export function startVersionCheck() {
  const mine = currentBundle();
  if (!mine) return; // dev server: no fingerprinted bundle

  let stale = false;
  const check = async () => {
    if (stale) return;
    const latest = await latestBundle();
    if (latest && latest !== mine) {
      stale = true;
      if (document.visibilityState === 'hidden') return; // reload when they come back (below)
      showRefreshPill();
    }
  };

  document.addEventListener('visibilitychange', async () => {
    if (document.visibilityState !== 'visible') return;
    if (stale) { window.location.reload(); return; }
    await check();
  });
  window.setInterval(check, CHECK_EVERY_MS);
  window.setTimeout(check, 15_000);
}
